"use client";

import React from "react";
import type { Locale } from "~/i18n-config";
import type { getDictionary } from "~/get-dictionary";
import { AccountNavigation } from "../navigation/AccountNavigation";
import { RiAccountCircleFill } from "react-icons/ri";
import { IoMdBusiness } from "react-icons/io";
import {
  MdLocalHospital,
  MdOutlineManageAccounts,
  MdAccountCircle,
  MdLogout,
} from "react-icons/md";
import { ServerAuthProviderResponse } from "~/components/providers/server/AuthProvider";
import { ServerAccountProviderResponse } from "@/components/providers/server/AccountProvider";
import { LangLink } from "~/components/locales/LangLink";
import { InlineText } from "@/components/content/common/InlineText";

export function HeaderAccountView({
  lang,
  auth,
  account,
  layout,
  companyNav,
  dictionary,
}: {
  lang: Locale;
  auth: ServerAuthProviderResponse;
  account: ServerAccountProviderResponse;
  layout?: string | null;
  companyNav?: React.ReactNode;
  dictionary: HeaderAccountDictionary;
}) {
  if (auth.isLoggedIn && auth.user && account) {
    return (
      <div className="hidden lg:flex flex-row gap-4">
        {auth.hasPermBusiness && layout === "business" && companyNav
          ? companyNav
          : null}
        <AccountNavigation
          lang={lang}
          dictionary={{
            account: dictionary.account,
            navAccountAdmin: dictionary.navAccountAdmin,
            navAccountBusiness: dictionary.navAccountBusiness,
            navAccountHome: dictionary.navAccountHome,
            navAccountLogout: dictionary.navAccountLogout,
            navAccountSettings: dictionary.navAccountSettings,
          }}
          firstname={
            layout === "business" ? undefined : account.firstname || ""
          }
          lastname={layout === "business" ? undefined : account.lastname || ""}
          hasPermAdmin={auth.hasPermAdmin}
          hasPermAgent={auth.hasPermAgent}
          hasPermBusiness={auth.hasPermBusiness}
          layout={layout}
        />
      </div>
    );
  }

  return (
    <div className="hidden lg:flex lg:flex-row lg:flex-wrap lg:gap-4 lg:items-center lg:justify-center">
      <LangLink
        lang={lang}
        href="/signup"
        className="group gap-2 items-center justify-center rounded-lg py-3 px-5 text-normal font-jost font-semibold outline-2 outline-offset-2 transition-colors text-white bg-black hover:bg-black/80 active:bg-zinc-800/60"
      >
        {dictionary.navAccountSignupLong}
      </LangLink>
      <LangLink
        lang={lang}
        href="/login"
        className="group justify-center box-border rounded-md py-2 px-5 text-normal font-jost font-semibold border-0 outline outline-offset-2 outline-white text-white transition-colors hover:text-zinc-800 hover:bg-white/90"
      >
        {dictionary.navAccountLogin}
      </LangLink>
    </div>
  );
}

export function HeaderAccountMobileView({
  lang,
  auth,
  layout,
  signup,
  companyNavMobile,
  dictionary,
}: {
  lang: Locale;
  auth: ServerAuthProviderResponse;
  layout?: string | null;
  signup?: boolean;
  companyNavMobile?: React.ReactNode;
  dictionary: HeaderAccountDictionary;
}) {
  if (auth.isLoggedIn) {
    if (signup) {
      return (
        <div>
          <div className="flex flex-row gap-2 w-full p-3">
            <form action="/auth/logout" method="post" className="w-full">
              <input type="hidden" name="lang" value={lang} />
              <button className="inline-flex items-center justify-start gap-2 w-full rounded-lg py-3 px-4 text-normal text-center font-jost font-normal transition-colors text-white outline-2 outline-zinc-200 bg-zinc-800 hover:bg-zinc-800/80 border-0">
                <MdLogout
                  size="20px"
                  color="#ffffff"
                  className="flex group-hover:hidden"
                />
                <span>{dictionary.navAccountLogout}</span>
              </button>
            </form>
          </div>
        </div>
      );
    }

    return (
      <div>
        {auth.hasPermBusiness && layout === "business" && companyNavMobile
          ? companyNavMobile
          : null}
        <div className="flex flex-row gap-2 w-full pt-3 pl-3 pr-3">
          <LangLink
            lang={lang}
            href="/dashboard"
            className="inline-flex items-center justify-start gap-2 w-full truncate align-middle rounded-lg py-3 px-4 text-normal text-center font-jost font-semibold transition-colors bg-zinc-200 hover:bg-zinc-200/80 text-zinc-800"
          >
            <MdLocalHospital size="20px" color="#27272a" />
            <span>{dictionary.navAccountHome}</span>
          </LangLink>
        </div>
        {auth.hasPermBusiness && layout !== "business" && (
          <div className="flex flex-row gap-2 w-full pt-3 pl-3 pr-3">
            <LangLink
              lang={lang}
              href="/business"
              className="inline-flex items-center justify-start gap-2 w-full truncate align-middle rounded-lg py-3 px-4 text-normal text-center font-jost font-semibold transition-colors bg-zinc-200 hover:bg-zinc-200/80 text-zinc-800"
            >
              <IoMdBusiness size="20px" color="#27272a" />
              <span>
                <InlineText value={dictionary.navAccountBusiness} />
              </span>
            </LangLink>
          </div>
        )}
        {(auth.hasPermAdmin || auth.hasPermAgent) &&
          layout !== "management" && (
            <div className="flex flex-row gap-2 w-full pt-3 pl-3 pr-3">
              <LangLink
                lang={lang}
                href="/management"
                className="inline-flex items-center justify-start gap-2 w-full truncate align-middle rounded-lg py-3 px-4 text-normal text-center font-jost font-semibold transition-colors bg-zinc-200 hover:bg-zinc-200/80 text-zinc-800"
              >
                <MdOutlineManageAccounts size="20px" color="#27272a" />
                <span>{dictionary.navAccountAdmin}</span>
              </LangLink>
            </div>
          )}
        <div className="flex flex-row gap-2 w-full p-3">
          <LangLink
            lang={lang}
            href="/account"
            className="inline-flex items-center justify-start gap-2 w-3/5 truncate align-middle rounded-lg py-3 px-4 text-normal text-center font-jost font-semibold transition-colors bg-zinc-200 hover:bg-zinc-200/80 text-zinc-800"
          >
            <RiAccountCircleFill size="20px" color="#27272a" />
            <span>{dictionary.navAccountSettings}</span>
          </LangLink>
          <form action="/auth/logout" method="post" className="w-2/5">
            <input type="hidden" name="lang" value={lang} />
            <button className="inline-flex items-center justify-start gap-2 w-full rounded-lg py-3 px-4 text-normal text-center font-jost font-normal transition-colors text-white outline-2 outline-zinc-200 bg-zinc-800 hover:bg-zinc-800/80 border-0">
              <MdLogout
                size="20px"
                color="#ffffff"
                className="flex group-hover:hidden"
              />
              <span>{dictionary.navAccountLogout}</span>
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-row gap-2 w-full p-3">
        <LangLink
          lang={lang}
          href="/signup"
          className="flex gap-2 items-center justify-center w-full rounded-lg py-3 px-4 text-normal text-center font-jost font-semibold outline-2 outline-offset-2 transition-colors text-white bg-black hover:bg-zinc-800 active:text-white active:bg-black/60"
        >
          <MdAccountCircle size="20px" color="#ffffff" />
          {dictionary.navAccountSignup}
        </LangLink>
        <LangLink
          lang={lang}
          href="/login"
          className="flex gap-2 items-center justify-center w-full rounded-lg py-3 px-4 text-normal text-center font-jost font-semibold outline-2 outline-offset-2 transition-colors text-zinc-800 bg-zinc-200 hover:bg-zinc-100 active:bg-zinc-200/60"
        >
          {dictionary.navAccountLogin}
        </LangLink>
      </div>
    </div>
  );
}

export type HeaderAccountDictionary = Pick<
  Awaited<ReturnType<typeof getDictionary>>,
  | "account"
  | "navAccountAdmin"
  | "navAccountBusiness"
  | "navAccountHome"
  | "navAccountLogout"
  | "navAccountSettings"
  | "navAccountSignupLong"
  | "navAccountLogin"
  | "navAccountSignup"
>;
